/**
 * Writes text that is still being produced (a server render) to the clipboard
 * INSIDE the user's click: the write is issued now with a pending Blob, which
 * Chrome holds while the text resolves. A writeText issued seconds after the
 * click can neither resolve nor reject (measured in real Chromium 2026-09-30,
 * extension useCopyFindings.ts), so awaiting the render first risks a copy
 * that never lands.
 *
 * Where ClipboardItem is missing or the write is refused, `fallback` gets the
 * resolved text: the caller's existing writeText/legacy path, which reports
 * success or failure, so the outcome is never silent. `text` must not reject;
 * callers fold their own failure into a local format first.
 *
 * `html` (2026-10-28, Emily "proceed"): the same content as HTML, written as
 * text/html on the SAME item, so a rich editor pastes a formatted issue with its
 * screenshot as an image while plain boxes keep the text. Never image/png: an
 * item with a picture and no HTML pastes only the picture into a rich editor
 * (docs/test-reports/2026-10-28-copy-paste-spike). The item's types are fixed
 * now, inside the click, so a caller that may get HTML passes the promise and
 * resolves it null when none came (a failed render). That part then carries the
 * escaped plain text in a preformatted block, preserving the already-authorized
 * write even when the server fails after the click expires. `html` must not reject.
 */
export async function writeClipboardTextDeferred(text, fallback, html) {
    if (typeof ClipboardItem !== 'undefined' && typeof navigator.clipboard?.write === 'function') {
        try {
            const parts = { 'text/plain': text.then((t) => new Blob([t], { type: 'text/plain' })) };
            if (html) {
                parts['text/html'] = html.then(async (h) => new Blob([
                    h || `<pre>${(await text).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')}</pre>`,
                ], { type: 'text/html' }));
            }
            await navigator.clipboard.write([new ClipboardItem(parts)]);
            return true;
        }
        catch {
            // Refused (permission or focus): the fallback below reports the outcome.
        }
    }
    return fallback(await text);
}
//# sourceMappingURL=clipboardWrite.js.map